import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/base/PageHeader";
import { TableSkeleton } from "@/components/base/TableSkeleton";
import { EmptyState } from "@/components/base/EmptyState";
import { fmtDateTime } from "@/lib/format";
import { isNotifTypeEnabled } from "@/lib/preferences";

interface Notif {
  id: string;
  type: string;
  title: string;
  message: string | null;
  related_type: string | null;
  related_id: string | null;
  is_read: boolean;
  created_at: string;
}

function getNotifRoute(notif: Notif): string | null {
  if (!notif.related_type || !notif.related_id) return null;
  const routes: Record<string, string> = {
    product: `/products/${notif.related_id}`,
    products: `/products/${notif.related_id}`,
    sales_order: `/sales-orders/${notif.related_id}`,
    sales_orders: `/sales-orders`,
    purchase_order: `/purchase-orders/${notif.related_id}`,
    purchase_orders: `/purchase-orders`,
    shipment: `/fulfilment/shipments/${notif.related_id}`,
    shipments: `/fulfilment?tab=shipments`,
    return: `/returns/${notif.related_id}`,
    returns: `/returns`,
    transfer: `/transfers/${notif.related_id}`,
    inventory: `/inventory`,
    low_stock: `/inventory`,
  };
  return routes[notif.related_type] ?? null;
}

const typeDot: Record<string, string> = {
  warning: "bg-amber-500",
  error: "bg-red-500",
  success: "bg-emerald-500",
  low_stock: "bg-amber-500",
  po_overdue: "bg-red-500",
  return_pending: "bg-amber-500",
  ready_to_ship: "bg-emerald-500",
  replenishment_due: "bg-amber-500",
};

export default function Notifications() {
  const { org, user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<"all" | "unread">("all");

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["notifications-page", org?.id],
    enabled: !!org?.id,
    queryFn: async () => {
      const { data } = await supabase
        .from("notifications")
        .select("id, type, title, message, related_type, related_id, is_read, created_at")
        .eq("org_id", org!.id)
        .order("created_at", { ascending: false })
        .limit(200);
      return (data ?? []) as Notif[];
    },
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["notifications-page", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["notifications", org?.id, user?.id] });
  };

  const markRead = async (id: string) => {
    await supabase.from("notifications").update({ is_read: true }).eq("id", id);
    invalidate();
  };

  const markAllRead = async () => {
    await supabase.from("notifications").update({ is_read: true }).eq("org_id", org!.id).eq("is_read", false);
    invalidate();
  };

  const all = (data ?? []).filter((n) => isNotifTypeEnabled(n.type));
  const unread = all.filter((n) => !n.is_read).length;
  const list = filter === "unread" ? all.filter((n) => !n.is_read) : all;

  return (
    <div>
      <PageHeader title="Notifications" subtitle="Operational alerts and attention items">
        <button
          onClick={markAllRead}
          disabled={unread === 0}
          className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
        >
          <i className="ri-check-double-line" />
          Mark all read
        </button>
      </PageHeader>

      <div className="mb-4 flex items-center gap-1 bg-slate-100 rounded-full p-1 w-fit">
        <button
          onClick={() => setFilter("all")}
          className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
            filter === "all" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`}
        >
          All
        </button>
        <button
          onClick={() => setFilter("unread")}
          className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
            filter === "unread" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`}
        >
          Unread ({unread})
        </button>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8">
          <TableSkeleton />
        </div>
      ) : isError ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-500 mb-4">Couldn't load notifications.</p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap"
          >
            Retry
          </button>
        </div>
      ) : list.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200">
          <EmptyState
            icon="ri-notification-3-line"
            title={filter === "unread" ? "All caught up" : "No notifications"}
            description={filter === "unread" ? "You have no unread notifications." : "Operational alerts will appear here."}
          />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="divide-y divide-slate-50">
            {list.map((n) => {
              const route = getNotifRoute(n);
              return (
                <div key={n.id} className={`px-4 py-3.5 flex items-start gap-3 ${!n.is_read ? "bg-slate-50/50" : ""}`}>
                  <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${typeDot[n.type] ?? "bg-slate-400"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-medium text-slate-800">{n.title}</p>
                      {!n.is_read && (
                        <span className="text-[10px] font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full whitespace-nowrap">New</span>
                      )}
                    </div>
                    {n.message && <p className="text-sm text-slate-500 mt-0.5 leading-relaxed">{n.message}</p>}
                    <div className="flex items-center gap-3 mt-1">
                      <p className="text-xs text-slate-400">{fmtDateTime(n.created_at)}</p>
                      {route && (
                        <button
                          onClick={() => { if (!n.is_read) markRead(n.id); navigate(route); }}
                          className="text-xs text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1 whitespace-nowrap"
                        >
                          View <i className="ri-arrow-right-line" />
                        </button>
                      )}
                    </div>
                  </div>
                  {!n.is_read && (
                    <button
                      onClick={() => markRead(n.id)}
                      className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-emerald-600 transition-colors"
                      title="Mark read"
                    >
                      <i className="ri-check-line" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}